import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import {
  Container,
  Button,
  IconButton,
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { TRUNG_THU_LOAI_BANH, TRUNG_THU_LOAI_PHIEU } from "../../constants/trungThu";
import TrungThuQrScannerModal from "./TrungThuQrScannerModal";

const toValidStoreId = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

// Mock data as fallback
const mockReceiptData = {
  id: 1,
  soPhieu: "Điền vào",
  ngay: new Date(2024, 5, 4),
  khachHang: "Tên store",
  items: [],
};

const isTruthyFlag = (value) =>
  value === true || value === 1 || value === "1";

const isStatusValue = (value, expected) => Number(value) === expected;

const CUSTOM_STORE_OPTIONS = [
  { IDCH: 222, TenCuaHang: "Khách hàng" },
  { IDCH: 333, TenCuaHang: "Biếu tặng" },
];

const requiresCustomerName = (storeId) =>
  ["222", "333"].includes(String(storeId).trim());

const STORE_RECEIVE_TIMEOUT_MS = 24 * 60 * 60 * 1000;

const numberFormatter = new Intl.NumberFormat("vi-VN");
const currencyFormatter = new Intl.NumberFormat("vi-VN", {
  style: "currency",
  currency: "VND",
  maximumFractionDigits: 0,
});

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const calculateReceiptTotals = (items = []) =>
  items.reduce(
    (totals, item) => {
      const quantity = toFiniteNumber(item?.soLuong);
      const persistedAmount = Number(item?.thanhTien);
      const amount =
        item?.thanhTien !== null &&
        item?.thanhTien !== undefined &&
        item?.thanhTien !== "" &&
        Number.isFinite(persistedAmount)
          ? persistedAmount
          : quantity * toFiniteNumber(item?.giaMua);

      totals.quantity += quantity;
      totals.amount += amount;
      return totals;
    },
    { quantity: 0, amount: 0 }
  );

const isTransferReceiptData = (receiptData) => {
  const ghiChu = receiptData?.ghiChu ?? receiptData?.GhiChu;
  if (ghiChu !== "DieuChuyen") {
    return false;
  }

  const loaiPhieu = receiptData?.loaiPhieu ?? receiptData?.LoaiPhieu;
  if (
    loaiPhieu === null ||
    loaiPhieu === undefined ||
    String(loaiPhieu).trim() === ""
  ) {
    // sale-nhap-hang-detail can omit LoaiPhieu; this component is Trung Thu only.
    return true;
  }

  return Number(loaiPhieu) === TRUNG_THU_LOAI_PHIEU;
};

const isReturnReceiptData = (receiptData) => {
  const ghiChu = String(
    receiptData?.ghiChu ?? receiptData?.GhiChu ?? ""
  )
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");

  return ghiChu.includes("trave");
};

const getCurrentConfirmUser = (fallback = "User") =>
  String(localStorage.getItem("TenUser") || fallback).trim();

const getApiConfirmUser = (source) =>
  String(
    source?.NguoiXacNhan ?? source?.nguoiXacNhan ?? source?.nguoixacnhan ?? ""
  ).trim();

const getApiTenUser = (source) =>
  String(source?.TenUser ?? source?.tenUser ?? source?.tenuser ?? "").trim();

const getFirstConfirmUser = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const prefixConfirmUser = (message, userName) => {
  const normalizedUserName = String(userName || "").trim();
  return normalizedUserName ? `${normalizedUserName} - ${message}` : message;
};

const parseDateTime = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const rawValue = String(value).trim();
  if (!rawValue) return null;

  const normalizedValue = rawValue.split("!")[0].trim();
  const compactDateMatch = normalizedValue.match(
    /^(\d{4})(\d{2})(\d{2})(\d{2})?(\d{2})?(\d{2})?$/
  );

  if (compactDateMatch && ["19", "20"].includes(compactDateMatch[1].slice(0, 2))) {
    const [, year, month, day, hour = "00", minute = "00", second = "00"] =
      compactDateMatch;
    const compactDate = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    );
    return Number.isNaN(compactDate.getTime()) ? null : compactDate;
  }

  const englishMonthDateMatch = normalizedValue.match(
    /^([A-Za-z]{3,9})\s+(\d{1,2})\s+(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i
  );

  if (englishMonthDateMatch) {
    const monthMap = {
      jan: 0,
      january: 0,
      feb: 1,
      february: 1,
      mar: 2,
      march: 2,
      apr: 3,
      april: 3,
      may: 4,
      jun: 5,
      june: 5,
      jul: 6,
      july: 6,
      aug: 7,
      august: 7,
      sep: 8,
      sept: 8,
      september: 8,
      oct: 9,
      october: 9,
      nov: 10,
      november: 10,
      dec: 11,
      december: 11,
    };
    const [, monthText, day, year, rawHour, minute, second = "00", meridiem] =
      englishMonthDateMatch;
    const month = monthMap[monthText.toLowerCase()];
    if (month !== undefined) {
      let hour = Number(rawHour);
      if (meridiem.toUpperCase() === "PM" && hour !== 12) hour += 12;
      if (meridiem.toUpperCase() === "AM" && hour === 12) hour = 0;

      const englishMonthDate = new Date(
        Number(year),
        month,
        Number(day),
        hour,
        Number(minute),
        Number(second)
      );
      return Number.isNaN(englishMonthDate.getTime())
        ? null
        : englishMonthDate;
    }
  }

  if (/^\d{13}$/.test(normalizedValue)) {
    const timestampDate = new Date(Number(normalizedValue));
    return Number.isNaN(timestampDate.getTime()) ? null : timestampDate;
  }

  if (/^\d{10}$/.test(normalizedValue)) {
    const timestampDate = new Date(Number(normalizedValue) * 1000);
    return Number.isNaN(timestampDate.getTime()) ? null : timestampDate;
  }

  const parsedDate = new Date(normalizedValue);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatElapsedDuration = (startAt, endAt) => {
  const startDate = parseDateTime(startAt);
  const endDate = parseDateTime(endAt);
  if (!startDate || !endDate) return "";

  const diffMs = Math.max(0, endDate.getTime() - startDate.getTime());
  const totalMinutes = Math.min(
    Math.floor(diffMs / (1000 * 60)),
    STORE_RECEIVE_TIMEOUT_MS / (1000 * 60)
  );
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${hours} giờ ${minutes} phút`;
};

const getWaitingTimeToneClass = (startAt, endAt) => {
  const startDate = parseDateTime(startAt);
  const endDate = parseDateTime(endAt);

  if (!startDate || !endDate) return "waiting-neutral";

  const totalMinutes = Math.floor(
    Math.max(0, endDate.getTime() - startDate.getTime()) / (1000 * 60)
  );

  if (totalMinutes <= 30) return "waiting-cool";
  if (totalMinutes <= 120) return "waiting-fresh";
  if (totalMinutes <= 360) return "waiting-warm";
  if (totalMinutes <= 720) return "waiting-hot";
  return "waiting-critical";
};

const getTransferReceiptStatus = ({ actPhieu, status }) => {
  if (!isTruthyFlag(actPhieu)) {
    return "Chờ admin xác nhận";
  }

  if (isStatusValue(status, 1)) {
    return "Hoàn tất";
  }

  return "Chờ cửa hàng nhận";
};

const PhieuNhapTT = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState("user");
  const [userInfo, setUserInfo] = useState({});
  const [isEditMode, setIsEditMode] = useState(false); // New state to track edit mode
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");
  const [trangThaii, setTrangThai] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isConfirming, setIsConfirming] = useState(false);
  // Add state for button confirmations
  const [bqdConfirmed, setBqdConfirmed] = useState(false);
  const [bqdTimestamp, setBqdTimestamp] = useState(null);
  const [bqdConfirmedBy, setBqdConfirmedBy] = useState("");
  const [receiverConfirmed, setReceiverConfirmed] = useState(false);
  const [receiverTimestamp, setReceiverTimestamp] = useState(null);
  const [receiverConfirmedBy, setReceiverConfirmedBy] = useState("");
  const [storeConfirmed, setStoreConfirmed] = useState(false);
  const [storeTimestamp, setStoreTimestamp] = useState(null);
  const [stores, setStores] = useState([]);
  const [storeName, setStoreName] = useState("");
  const [senderName, setSenderName] = useState(""); // tên cửa hàng gửi (MaCH_DC khi ĐiềuChuyển)
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // --- new: state for "edit store" modal ---
  const [openStoreModal, setOpenStoreModal] = useState(false);
  const [storeOptions, setStoreOptions] = useState([]);
  const [storeOptionsLoading, setStoreOptionsLoading] = useState(false);
  const [storeSearch, setStoreSearch] = useState("");
  const [selectedStoreMaCH, setSelectedStoreMaCH] = useState("");
  const [customerName, setCustomerName] = useState("");
  // -----------------------------------------
  const [openTransferReasonModal, setOpenTransferReasonModal] = useState(false);
  const [transferReason, setTransferReason] = useState("");

  // QR Code processing function
  const processQRCode = async (qrData, { showErrorModal = true } = {}) => {
    try {
      let loSX = "";
      let quantity = 1; // Default quantity

      // Parse quantity and LoSX from input string
      if (qrData.includes("doticom.vn/t.php?c=")) {
        // Check if there's a quantity prefix (e.g., "10https://doticom.vn/t.php?c=509")
        const urlMatch = qrData.match(/(\d*)https?:\/\/doticom\.vn\/t\.php\?c=(\d+)/);
        if (urlMatch) {
          const quantityPrefix = urlMatch[1];
          loSX = urlMatch[2];
          if (quantityPrefix && quantityPrefix.length > 0) {
            quantity = parseInt(quantityPrefix, 10) || 1;
          }
        } else {
          // Fallback to original URL parsing
          const match = qrData.match(/c=(\d+)/);
          if (match) loSX = match[1];
        }
      } else if (/^\d+$/.test(qrData.trim())) {
        // Pure numeric string (LoSX only)
        loSX = qrData.trim();
        quantity = 1;
      } else {
        // Check if it's a quantity + LoSX combination without URL
        const numericMatch = qrData.match(/^(\d+)(\d{2,})$/);
        if (numericMatch && numericMatch[2].length >= 2) {
          // If second group has at least 2 digits, treat first as quantity
          quantity = parseInt(numericMatch[1], 10) || 1;
          loSX = numericMatch[2];
        } else {
          // Fallback: treat entire string as LoSX
          loSX = qrData.trim();
        }
      }

      if (!loSX) throw new Error("Không thể trích xuất mã LoSX từ QR code");

      // Call QRTrungThu API
      const qrRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/QRTrungThu?LoSX=${loSX}`
      );
      if (!qrRes.ok) throw new Error("Lỗi API QRTrungThu");
      let qrItems = await qrRes.json();

      // Ensure qrItems is always an array
      if (!Array.isArray(qrItems)) {
        if (qrItems && typeof qrItems === "object") {
          qrItems = [qrItems];
        } else {
          qrItems = [];
        }
      }

      if (!qrItems || qrItems.length === 0)
        throw new Error("Không tìm thấy sản phẩm với mã LoSX này");

      for (const qrItem of qrItems) {
        const { MaSP, GiaMua } = qrItem;
        const itemRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/items/${MaSP}`
        );
        const itemData = itemRes.ok ? await itemRes.json() : [];
        const Ten_SP = itemData[0]?.Ten_SP || `Sản phẩm ${MaSP}`;

        setReceipt((prev) => {
          if (!prev) return prev;
          const idx = prev.items.findIndex((item) => item.maVt === MaSP);
          let updatedItems;
          if (idx >= 0) {
            updatedItems = [...prev.items];
            updatedItems[idx] = {
              ...updatedItems[idx],
              soLuong: updatedItems[idx].soLuong || 0,  // Giữ nguyên, không cộng thêm
              slThucNhan: (parseInt(updatedItems[idx].slThucNhan) || 0) + quantity,
              loSX: updatedItems[idx].loSX || loSX,
            };
          } else {
            updatedItems = [
              ...prev.items,
              {
                maVt: MaSP,
                ten: Ten_SP,
                soLuong: 0,  // Đổi từ quantity thành 0
                slThucNhan: quantity,  // Chỉ SL Thực Nhận mới nhận giá trị từ QR
                dvt: "cái",
                giaMua: GiaMua || 0,
                isQRAdded: true,
                loSX: loSX,
              },
            ];
          }
          return { ...prev, items: updatedItems };
        });
      }

      const successMessage = `Đã thêm ${qrItems.length} sản phẩm với số lượng ${quantity} từ QR code (LoSX: ${loSX})`;
      setSnackbarMessage(successMessage);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      return { success: true, message: successMessage };

    } catch (error) {
      const errorMessage = `Lỗi xử lý QR code: ${error.message}`;
      if (showErrorModal) {
        setErrorModalMessage(errorMessage);
        setErrorModalOpen(true);
      }
      return { success: false, message: errorMessage };
    }
  };

  // --- new: helpers to normalize store object for dropdown ---
  const getStoreIdValue = (s) => {
    if (!s || typeof s !== "object") return "";
    const idKeys = ["MaCH","Ma_CuaHang","MaCH_","IDCH","id","Id","MaCuaHang","Ma_CH"];
    for (const k of idKeys) {
      if (k in s && s[k] !== null && s[k] !== undefined && String(s[k]).trim() !== "") {
        return String(s[k]).trim();
      }
    }
    // Fallback numeric compare
    for (const k of idKeys) {
      if (k in s && !isNaN(s[k])) return String(s[k]);
    }
    return "";
  };

  const getStoreDisplayName = (s) => {
    if (!s || typeof s !== "object") return "";
    const nameKeys = ["TenCuaHang","Ten_CH","TenCH","Ten","name","ten","Ten_CuaHang"];
    for (const k of nameKeys) {
      if (k in s && s[k]) return String(s[k]);
    }
    if (s.Ten) return String(s.Ten);
    if (s.name) return String(s.name);
    return "";
  };
  // -----------------------------------------------------------

  // Helper: find store display name by MaCH (works with various API shapes)
  const getStoreNameByMaCH = (
    maCH,
    storesList,
    { fallbackToLocalStorage = true } = {}
  ) => {
    try {
      if (String(maCH).trim() === "222") {
        return "Khách hàng";
      }
      if (String(maCH).trim() === "333") {
        return "Biếu tặng";
      }
      if (String(maCH).trim() === "444") {
        return "Trả hủy";
      }
      if (String(maCH).trim() === "555") {
        return "Kho DC";
      }
      if (String(maCH).trim() === "666") {
        return "Kho Thanh Lý";
      }
      // Prefer provided list (from fetch), fallback to component state
      const list = Array.isArray(storesList)
        ? storesList
        : Array.isArray(stores)
          ? stores
          : [];
      if (!maCH && !list.length) {
        return fallbackToLocalStorage ? localStorage.getItem("TenCuaHang") || "" : "";
      }

      // Normalize maCH to string for comparisons
      const target =
        maCH !== null && maCH !== undefined ? String(maCH).trim() : "";

      if (target === "") {
        return fallbackToLocalStorage ? localStorage.getItem("TenCuaHang") || "" : "";
      }

      const nameKeys = [
        "TenCuaHang",
        "Ten_CH",
        "TenCH",
        "Ten",
        "name",
        "ten",
        "Ten_CuaHang",
      ];
      const idKeys = [
        "MaCH",
        "Ma_CuaHang",
        "MaCH_",
        "IDCH",
        "id",
        "Id",
        "MaCuaHang",
        "Ma_CH",
      ];

      // Try to find a matching store by various possible id fields
      const found = list.find((s) => {
        if (!s || typeof s !== "object") return false;

        // Try id-like fields
        for (const k of idKeys) {
          if (k in s && s[k] !== null && s[k] !== undefined) {
            if (String(s[k]).trim() === target) return true;
          }
        }

        // Some APIs might expose numeric id while maCH is numeric string
        for (const k of idKeys) {
          if (k in s && !isNaN(s[k]) && !isNaN(target)) {
            if (Number(s[k]) === Number(target)) return true;
          }
        }

        return false;
      });

      if (found) {
        for (const k of nameKeys) {
          if (k in found && found[k]) return String(found[k]);
        }
        // Last resort: return one of common fields
        if (found.Ten) return String(found.Ten);
        if (found.name) return String(found.name);
      }

      // If no match, use localStorage only for destination-store fallbacks.
      return fallbackToLocalStorage
        ? localStorage.getItem("TenCuaHang") || target || ""
        : target || "";
    } catch (err) {
      console.error("Error in getStoreNameByMaCH:", err);
      return fallbackToLocalStorage
        ? localStorage.getItem("TenCuaHang") || (maCH ? String(maCH) : "")
        : maCH
          ? String(maCH)
          : "";
    }
  };

  // Get user role from localStorage
  const getUserRole = () => {
    const maPB = localStorage.getItem("MaPB");
    const isAdmin = localStorage.getItem("isAdmin") === "true";
    const userIDCH = localStorage.getItem("IDCH");

    const userInfo = {
      maPB: maPB,
      isAdmin: isAdmin,
      idCH: userIDCH,
    };

    setUserInfo(userInfo);

    // Admin permissions
    if (isAdmin && ["4", "321", "145", "170", "187", "250", "16", "11", "321"].includes(maPB)) {
      return "admin";
    }

    // Coordinator permissions
    if (["80", "53", "4", "321", "16"].includes(maPB)) {
      return "coordinator";
    }

    // Regular user
    return "user";
  };

  useEffect(() => {
    // Check for admin privileges and user role
    try {
      const role = getUserRole();
      setUserRole(role);
      setIsAdmin(role === "admin");
    } catch (error) {
      console.error("Error checking user role:", error);
      setUserRole("user");
      setIsAdmin(false);
    }

    // Check if we`re in edit mode based on URL
    const currentPath = window.location.pathname;
    setIsEditMode(currentPath.includes("/edit"));

    // Initialize receipt with basic info, items will be loaded from API
    setReceipt({
      id: parseInt(id),
      soPhieu: `PN${id}`,
      ngay: new Date(),
      khachHang: localStorage.getItem("TenCuaHang") || "Tên store",
      items: [], // Sẽ được cập nhật từ API
      trangThai: trangThaii,
      receiverConfirmed: true,
      receiverTimestamp: new Date(),
      receiverMessage: "Đã xác nhận gửi phiếu",
      // lưu thêm cho trường hợp ĐiềuChuyển (mặc định rỗng, sẽ cập nhật từ API)
      ghiChu: "",
      maCH_DC: null,
    });
  }, [id]);

  useEffect(() => {
    const fetchFixedItems = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/sp-phieu-nhap`
        );
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        const data = await response.json();
        console.log("📦 Fixed Items from API:", data);

        const mappedFixedItems = data.map((item) => ({
          maVt: item.MaSP,
          ten: item.Ten_SP,
          soLuong: null, // Will be filled from receipt details
          slThucNhan: null, // Will be filled from receipt details
          dvt: item.DVT_SP,
          giaMua: item.GiaMua,
        }));

        console.log("📦 Mapped Fixed Items:", mappedFixedItems);
        return mappedFixedItems;
      } catch (error) {
        console.error("Error fetching fixed items:", error);
        return [];
      }
    };

    const fetchStores = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/stores`);
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        const data = await response.json();
        console.log("🏪 Stores from API:", data);
        setStores(data);
        return data;
      } catch (error) {
        console.error("Error fetching stores:", error);
        return [];
      }
    };

    const fetchReceiptDetails = async (maPN) => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/sale-nhap-hang-detail/${maPN}`
        );
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        const data = await response.json();
        console.log("📋 Receipt Details from API:", data);

        if (!data?.[0]) {
          throw new Error("Receipt not found");
        }

        // Extract MaCH, Status, GhiChu, MaCH_DC
        const maCH = data[0]?.MaCH;
        const status = data[0]?.Status;
        const ghiChu = data[0]?.GhiChu;
        const tenKH = data[0]?.TenKH || "";
        const maCH_DC = data[0]?.MaCH_DC;
        const loaiPhieu = data[0]?.LoaiPhieu;
        const actPhieu = isTruthyFlag(data[0]?.Act_phieu);
        const nguoiXacNhan = getApiConfirmUser(data[0]);
        const tenUser = getApiTenUser(data[0]);
        const ndXacNhan = String(data[0]?.NDXacNhan ?? "").trim();
        const bqdConfirmedAt = parseDateTime(data[0]?.TimeXacNhan);
        const isTransferReceipt = isTransferReceiptData({
          LoaiPhieu: loaiPhieu,
          GhiChu: ghiChu,
        });
        const displayTrangThai = isTransferReceipt
          ? getTransferReceiptStatus({ actPhieu, status })
          : actPhieu
            ? "Đã duyệt"
            : "Đã gửi phiếu";
        console.log("🏪 Extracted MaCH from receipt:", maCH);
        console.log("📊 Extracted Status from receipt:", status);
        console.log("📝 Extracted GhiChu from receipt:", ghiChu);
        console.log("🏬 Extracted MaCH_DC from receipt:", maCH_DC);

        setTrangThai(displayTrangThai);

        if (actPhieu) {
          setBqdConfirmed(true);
          setBqdTimestamp(bqdConfirmedAt);
          setBqdConfirmedBy(nguoiXacNhan);
        }
        setReceiverConfirmedBy(tenUser);

        setReceipt({
          trangThai: displayTrangThai,
          ngay: new Date(data[0].NgayNhap),
          thang: new Date(data[0].NgayNhap).getMonth() + 1,
          nam: new Date(data[0].NgayNhap).getFullYear(),
          soPhieu: data[0].Phieu,
          khachHang: data[0].MaCH,
          maCH: maCH, // Store MaCH for later use
          status,
          actPhieu,
          loaiPhieu,
          bqdApproved: actPhieu,
          bqdTimestamp: actPhieu ? bqdConfirmedAt : null,
          bqdMessage: actPhieu ? "PKD đã xác nhận" : null,
          nguoiXacNhan,
          ndXacNhan,
          tenUser,
          // lưu thêm ghi chú và mã cửa hàng điều chuyển
          ghiChu: ghiChu || "",
          tenKH,
          maCH_DC: maCH_DC ?? null,
        });

        const items = data[0].Items || [];

        // Check if store has confirmed - transfer receipts use Status only.
        const itemsWithSoLuong = items.filter(
          (item) => item.SLNhapDau && item.SLNhapDau > 0
        );

        const hasStoreConfirmed = isTransferReceipt
          ? isStatusValue(status, 1)
          : itemsWithSoLuong.length > 0 &&
            itemsWithSoLuong.every(
              (item) =>
                item.SLThucNhan !== null &&
                item.SLThucNhan !== undefined &&
                item.SLThucNhan !== "" &&
                item.SLThucNhan !== 0
            );

        console.log("📋 Store confirmation check:", {
          totalItems: items.length,
          itemsWithSoLuong: itemsWithSoLuong.length,
          itemsWithSLThucNhan: itemsWithSoLuong.filter(
            (item) =>
              item.SLThucNhan !== null &&
              item.SLThucNhan !== undefined &&
              item.SLThucNhan !== "" &&
              item.SLThucNhan !== 0
          ).length,
          hasStoreConfirmed,
          status,
        });

        if (hasStoreConfirmed) {
          setStoreConfirmed(true);
          setStoreTimestamp(new Date(data[0].NgayNhap));
        } else {
          setStoreConfirmed(false);
          setStoreTimestamp(null);
        }

        // Extract timestamp from IDTimeNhap
        let receiverTimestamp = null;
        if (data[0].IDTimeNhap) {
          // Remove characters from ! onwards (!47)
          receiverTimestamp = parseDateTime(data[0].IDTimeNhap);
          console.log("⏰ Extracted receiver timestamp:", receiverTimestamp);
        }

        // Map receipt details based on Status and Act_phieu
        const mappedReceiptDetails = items.map((item) => {
          // Format product name with LoSX if available
          const productName = item.LoSX 
            ? `${item.TenSP} (${item.LoSX})`
            : item.TenSP;

          if (actPhieu && isStatusValue(status, 2)) {
            // Act_phieu: true và Status: 2 - Giữ nguyên cột Số Lượng = SLNhapDau và SL Thực Nhận = SLThucNhan
            return {
              maVt: item.MaSP,
              ten: productName,
              soLuong: item.SLNhapDau, // Số Lượng = SLNhapDau
              slThucNhan: item.SLThucNhan, // SL Thực Nhận = SLThucNhan
              dvt: item.DVT || "cái",
              giaMua: item.GiaMua,
              slNhapDau: item.SLNhapDau,
              thanhTien: item.ThanhTien,
              loSX: item.LoSX, // Preserve LoSX for reference
            };
          } else if (!isStatusValue(status, 1)) {
            // Status khác 1: Hiển thị cột Số Lượng = SL Thực Nhận = SLNhapDau
            return {
              maVt: item.MaSP,
              ten: productName,
              soLuong: item.SLNhapDau, // Số Lượng = SLNhapDau
              slThucNhan: item.SLNhapDau, // SL Thực Nhận = SLNhapDau (same as soLuong)
              dvt: item.DVT || "cái",
              giaMua: item.GiaMua,
              slNhapDau: item.SLNhapDau,
              thanhTien: item.ThanhTien,
              loSX: item.LoSX, // Preserve LoSX for reference
            };
          } else {
            // Status = 1: Hiển thị cột Số Lượng = SLNhapDau và SL Thực Nhận = SLThucNhan
            return {
              maVt: item.MaSP,
              ten: productName,
              soLuong: item.SLNhapDau, // Số Lượng = SLNhapDau
              slThucNhan: item.SLThucNhan, // SL Thực Nhận = SLThucNhan (giữ nguyên giá trị từ API, kể cả 0)
              dvt: item.DVT || "cái",
              giaMua: item.GiaMua,
              slNhapDau: item.SLNhapDau,
              thanhTien: item.ThanhTien,
              loSX: item.LoSX, // Preserve LoSX for reference
            };
          }
        });

        console.log("📋 Mapped Receipt Details:", mappedReceiptDetails);
        return {
          items: mappedReceiptDetails,
          receiverTimestamp,
          maCH,
          status,
          ghiChu,
          tenKH,
          maCH_DC,
          actPhieu,
          loaiPhieu,
          nguoiXacNhan,
          ndXacNhan,
          tenUser,
          bqdTimestamp: bqdConfirmedAt,
        };
      } catch (error) {
        console.error("Error fetching receipt details:", error);
        return {
          items: [],
          receiverTimestamp: null,
          maCH: null,
          status: null,
          ghiChu: "",
          tenKH: "",
          maCH_DC: null,
          actPhieu: false,
          loaiPhieu: null,
          nguoiXacNhan: "",
          ndXacNhan: "",
          tenUser: "",
          bqdTimestamp: null,
        };
      }
    };

    const initializeReceipt = async () => {
      setLoading(true);
      try {
        const [fixedItems, receiptDetailsResult, storesList] =
          await Promise.all([
            fetchFixedItems(),
            fetchReceiptDetails(id),
            fetchStores(),
          ]);

        const {
          items: receiptDetails,
          receiverTimestamp: apiReceiverTimestamp,
          maCH,
          status: apiStatus,
          ghiChu,
          tenKH,
          maCH_DC,
          actPhieu,
          loaiPhieu,
          nguoiXacNhan,
          ndXacNhan,
          tenUser,
          bqdTimestamp: apiBqdTimestamp,
        } = receiptDetailsResult;

        console.log("🔄 Starting merge process...");
        console.log("🔄 Fixed Items count:", fixedItems.length);
        console.log("🔄 Receipt Details count:", receiptDetails.length);
        console.log("⏰ API Receiver Timestamp:", apiReceiverTimestamp);
        console.log("🏪 MaCH for store matching:", maCH);
        console.log("📊 API Status:", apiStatus);

        // Get the correct store name (destination)
        const correctStoreName = getStoreNameByMaCH(maCH, storesList);
        setStoreName(correctStoreName);

        const currentMaPB = localStorage.getItem("MaPB");
        const currentIsAdmin = localStorage.getItem("isAdmin") === "true";
        const currentStoreId = localStorage.getItem("IDCH");
        const canSeePendingTransfer =
          (currentIsAdmin && ["4", "321", "145", "170", "187", "250", "16", "11"].includes(currentMaPB)) ||
          ["80", "53", "4", "321", "16"].includes(currentMaPB);

        if (
          isTransferReceiptData({ loaiPhieu, ghiChu }) &&
          !actPhieu &&
          !canSeePendingTransfer &&
          String(maCH) === String(currentStoreId)
        ) {
          setSnackbarMessage("Phiếu điều chuyển đang chờ admin xác nhận");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
          setTimeout(() => navigate("/nhaphangtt"), 800);
          return;
        }

        // If ĐiềuChuyển, map sender (source) store name from MaCH_DC
        if (ghiChu === "DieuChuyen" && maCH_DC) {
          const sender = getStoreNameByMaCH(maCH_DC, storesList, {
            fallbackToLocalStorage: false,
          });
          setSenderName(sender || "");
        } else {
          setSenderName("");
        }

        // Merge fixed items with receipt details by matching product codes
        const mergedItems = fixedItems.map((fixedItem) => {
          // Find matching item in receipt details by comparing MaSP
          const detailItem = receiptDetails.find(
            (item) => item.maVt === fixedItem.maVt
          );

          console.log(`🔍 Processing ${fixedItem.maVt}:`, {
            fixedItem,
            detailItem,
            found: !!detailItem,
          });

          if (detailItem) {
            const merged = {
              ...fixedItem,
              soLuong: detailItem.soLuong,
              slThucNhan: detailItem.slThucNhan,
              ten: detailItem.ten || fixedItem.ten,
              dvt: detailItem.dvt || fixedItem.dvt,
              giaMua: detailItem.giaMua || fixedItem.giaMua,
            };
            console.log(`✅ Merged item for ${fixedItem.maVt}:`, merged);
            return merged;
          } else {
            // If not found in receipt details, keep the fixed item as is
            console.log(
              `❌ No detail found for ${fixedItem.maVt}, keeping as is`
            );
            return fixedItem;
          }
        });

        // Filter out items that don`t have soLuong (null or empty)
        const itemsWithQuantities = mergedItems.filter((item) => {
          console.log("Filtering item:", {
            maVt: item.maVt,
            soLuong: item.soLuong,
            slThucNhan: item.slThucNhan,
            apiStatus: apiStatus,
          });

          if (isStatusValue(apiStatus, 1)) {
            // For status = 1, include item if SLNhapDau > 0 (regardless of SLThucNhan value)
            const hasSLNhapDau = item.slNhapDau > 0;
            console.log("Should include:", hasSLNhapDau, {
              slNhapDau: item.slNhapDau,
              slThucNhan: item.slThucNhan,
            });
            return hasSLNhapDau;
          } else {
            return item.soLuong > 0;
          }
        });

        const additionalItems = receiptDetails.filter(
          (detailItem) =>
            !fixedItems.some((fixedItem) => fixedItem.maVt === detailItem.maVt)
        );

        console.log("➕ Additional items not in fixed list:", additionalItems);
        console.log(
          "🔍 Items filtered by quantities:",
          itemsWithQuantities.length,
          "out of",
          mergedItems.length
        );

        // Combine filtered items with additional items
        let finalItems = [...itemsWithQuantities, ...additionalItems];

        // Auto-fill logic based on status
        if (!isStatusValue(apiStatus, 1)) {
          // Status khác 1: không cần auto-fill vì slThucNhan đã được set = soLuong
          console.log("📊 Status ≠ 1: Using SLNhapDau for both columns");
        } else {
          console.log(
            "📊 Status = 1: Using original SLThucNhan values from API"
          );
          /*
          // Status = 1: Auto-fill slThucNhan with soLuong if it's empty (for all users, not just 'user` role)
          finalItems = finalItems.map((item) => {
            // Only auto-fill if item has soLuong and slThucNhan is empty/null
            if (
              item.soLuong &&
              (item.slThucNhan === null ||
                item.slThucNhan === undefined ||
                item.slThucNhan === "" ||
                item.slThucNhan === 0)
            ) {
              console.log(
                `🔄 Auto-filling slThucNhan for ${item.maVt}: ${item.soLuong}`
              );
              return { ...item, slThucNhan: item.soLuong };
            }
            return item;
            
          });
           */
        }

        console.log("🎯 FINAL ITEMS FOR DISPLAY:", finalItems);
        console.log("🎯 Final items count:", finalItems.length);

        // Log each final item with its quantities
        finalItems.forEach((item, index) => {
          console.log(`🎯 Final Item ${index + 1}:`, {
            maVt: item.maVt,
            ten: item.ten,
            soLuong: item.soLuong,
            slThucNhan: item.slThucNhan,
            dvt: item.dvt,
          });
        });

        setReceipt((prev) => ({
          ...prev,
          items: finalItems,
          khachHang: correctStoreName, // destination store name
          maCH: maCH,
          status: apiStatus,
          actPhieu,
          loaiPhieu,
          nguoiXacNhan,
          tenUser,
          receiverConfirmed: !!apiReceiverTimestamp,
          receiverTimestamp: apiReceiverTimestamp,
          bqdApproved: actPhieu,
          bqdTimestamp: actPhieu ? apiBqdTimestamp : null,
          bqdMessage: actPhieu ? "PKD đã xác nhận" : null,
          ndXacNhan,
          // lưu thêm ghi chú và mã CH điều chuyển để dùng render
          ghiChu: ghiChu || "",
          tenKH: tenKH || "",
          maCH_DC: maCH_DC ?? null,
        }));

        // Update the confirmation states
        if (apiReceiverTimestamp) {
          setReceiverConfirmed(true);
          setReceiverTimestamp(apiReceiverTimestamp);
          setReceiverConfirmedBy(tenUser);
        }
      } catch (error) {
        console.error("Error initializing receipt:", error);
        // Fallback to empty items array if API fails
        setReceipt((prev) => ({
          ...prev,
          items: [],
          khachHang: localStorage.getItem("TenCuaHang") || "Tên store",
        }));
      } finally {
        setLoading(false);
      }
    };

    initializeReceipt();
  }, [id]);

  useEffect(() => {
    // Initialize confirmation states from receipt data
    if (receipt) {
      setBqdConfirmed(receipt.bqdApproved || false);
      setBqdTimestamp(receipt.bqdTimestamp || null);
      setReceiverConfirmed(receipt.receiverConfirmed || false);
      setReceiverTimestamp(receipt.receiverTimestamp || null);
    }
  }, [receipt]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  const handleGoBack = () => {
    navigate("/nhaphangtt");
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEdit = () => {
    navigate(`/newphieunhaptt/${id}/edit`);
  };

  // Update status based on BQD approval
  const getReceiptStatus = () => {
    if (isTransferReceiptData(receipt)) {
      return getTransferReceiptStatus({
        actPhieu: receipt?.actPhieu || receipt?.bqdApproved || bqdConfirmed,
        status: receipt?.status,
      });
    }

    if (
      receipt?.trangThai === "Đã duyệt" ||
      receipt?.bqdApproved ||
      bqdConfirmed
    ) {
      return "Đã duyệt";
    }
    return "Đã gửi phiếu";
  };

  const isCurrentUserReceivingStore = () =>
    String(receipt?.maCH ?? "") ===
    String(userInfo?.idCH || localStorage.getItem("IDCH") || "");

  const isTransferReadyForStoreConfirmation = () =>
    isTransferReceiptData(receipt) &&
    isTruthyFlag(receipt?.actPhieu || receipt?.bqdApproved || bqdConfirmed) &&
    !isStatusValue(receipt?.status, 1);

  const shouldShowUserStoreConfirmation = () => {
    if (!isTransferReceiptData(receipt)) {
      return userRole === "user";
    }

    return (
      userRole === "user" &&
      isCurrentUserReceivingStore() &&
      isTruthyFlag(receipt?.actPhieu || receipt?.bqdApproved || bqdConfirmed)
    );
  };

  const shouldShowStoreConfirmationButton = () => {
    if (!isTransferReceiptData(receipt)) {
      return !storeConfirmed;
    }

    return isTransferReadyForStoreConfirmation();
  };

  const isStoreConfirmationComplete = () => {
    if (isTransferReceiptData(receipt)) {
      return isStatusValue(receipt?.status, 1);
    }

    return storeConfirmed;
  };

  const confirmAction = async (button, message, isBqdAction = false) => {
    if (button.classList.contains("confirmed")) {
      return;
    }

    // Nếu là hành động BQD (xác nhận kiểm soát), gọi API
    if (isBqdAction) {
      setIsConfirming(true);

      try {
        // Chuẩn bị dữ liệu cho API
        const chiTietSanPham = receipt.items
          .filter(
            (item) =>
              item.maVt && item.slThucNhan !== null && item.slThucNhan !== ""
          )
          .map((item) => ({
            maSP: item.maVt,
            slThucNhan: parseInt(item.slThucNhan) || 0,
            LoSX: item.loSX || "",
            LoaiBanh: TRUNG_THU_LOAI_BANH,
            LoaiPhieu: TRUNG_THU_LOAI_PHIEU,
          }));

        if (chiTietSanPham.length === 0) {
          setSnackbarMessage(
            "Vui lòng nhập số lượng thực nhận cho ít nhất một sản phẩm"
          );
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
          setIsConfirming(false);
          return;
        }

        const confirmUserName = getCurrentConfirmUser();
        const requestBody = {
          LoaiBanh: TRUNG_THU_LOAI_BANH,
          LoaiPhieu: TRUNG_THU_LOAI_PHIEU,
          maPhieuNhap: id,
          NguoiXacNhan: confirmUserName,
          chiTietSanPham: chiTietSanPham,
        };

        console.log("📤 Sending API request:", requestBody);

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/xac-nhan-kiem-soat`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          }
        );

        const responseData = await response.json();

        if (!response.ok) {
          throw new Error(responseData.message || "Failed to confirm control");
        }

        console.log("✅ API Response:", responseData);

        const now = new Date();
        const confirmedAt =
          parseDateTime(responseData?.data?.TimeXacNhan) ||
          parseDateTime(responseData?.TimeXacNhan) ||
          now;

        // Update states
        setBqdConfirmed(true);
        setBqdTimestamp(confirmedAt);
        setBqdConfirmedBy(confirmUserName);

        // Cập nhật UI sau khi API thành công
        button.textContent = "✓ Đã xác nhận";
        button.classList.add("confirmed");

        const statusDiv = button.nextElementSibling;
        statusDiv.textContent = message;
        statusDiv.style.color = "#2196F3";
        statusDiv.style.fontWeight = "bold";

        // Add timestamp
        const timestamp = now.toLocaleString("vi-VN");
        setTimeout(() => {
          statusDiv.textContent = message + "\n" + timestamp;
        }, 500);

        // Update receipt state
        setReceipt((prev) => {
          const isTransfer = isTransferReceiptData(prev);
          return {
            ...prev,
            actPhieu: true,
            status: isTransfer ? 2 : prev?.status,
            bqdApproved: true,
            bqdTimestamp: confirmedAt,
            bqdMessage: message,
            nguoiXacNhan: confirmUserName,
            trangThai: isTransfer ? "Chờ cửa hàng nhận" : "Đã duyệt",
            lastUpdated: now,
            updateCount: (prev.updateCount || 0) + 1,
          };
        });

        // Update localStorage
        try {
          const storedReceipts =
            JSON.parse(localStorage.getItem("phieuNhapList")) || [];
          const updatedReceipts = storedReceipts.map((r) => {
            if (r.id === parseInt(id)) {
              return {
                ...r,
                bqdApproved: true,
                bqdTimestamp: confirmedAt,
                bqdMessage: message,
                nguoiXacNhan: confirmUserName,
                status: isTransferReceiptData(r) ? 2 : r.status,
                trangThai: isTransferReceiptData(r)
                  ? "Chờ cửa hàng nhận"
                  : "Đã duyệt",
                lastUpdated: now,
                updateCount: (r.updateCount || 0) + 1,
              };
            }
            return r;
          });
          localStorage.setItem(
            "phieuNhapList",
            JSON.stringify(updatedReceipts)
          );
        } catch (error) {
          console.error("Error updating localStorage:", error);
        }

        // Show success message
        setSnackbarMessage(
          `Xác nhận kiểm soát thành công! Đã cập nhật ${responseData.data.soSanPhamCapNhat} sản phẩm.`
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } catch (error) {
        console.error("Error confirming control:", error);
        setSnackbarMessage(`Lỗi khi xác nhận kiểm soát: ${error.message}`);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setIsConfirming(false);
      }
    } else {
      // Xử lý hành động không phải BQD (như cũ)
      const confirmUserName = getCurrentConfirmUser();
      button.textContent = "✓ Đã xác nhận";
      button.classList.add("confirmed");

      const statusDiv = button.nextElementSibling;
      statusDiv.textContent = message;
      statusDiv.style.color = "#2196F3";
      statusDiv.style.fontWeight = "bold";

      const now = new Date();
      setReceiverConfirmedBy(confirmUserName);
      const timestamp = now.toLocaleString("vi-VN");
      setTimeout(() => {
        statusDiv.textContent = message + "\n" + timestamp;
      }, 500);

      // Update local state for non-BQD actions
      setReceipt((prev) => ({
        ...prev,
        receiverConfirmed: true,
        receiverTimestamp: now,
        receiverMessage: message,
        tenUser: confirmUserName,
        lastUpdated: now,
        updateCount: (prev.updateCount || 0) + 1,
      }));

      // Update localStorage for non-BQD actions
      try {
        const storedReceipts =
          JSON.parse(localStorage.getItem("phieuNhapList")) || [];
        const updatedReceipts = storedReceipts.map((r) => {
          if (r.id === parseInt(id)) {
            return {
              ...r,
              receiverConfirmed: true,
              receiverTimestamp: now,
              receiverMessage: message,
              tenUser: confirmUserName,
              lastUpdated: now,
              updateCount: (r.updateCount || 0) + 1,
            };
          }
          return r;
        });
        localStorage.setItem("phieuNhapList", JSON.stringify(updatedReceipts));
      } catch (error) {
        console.error("Error updating localStorage:", error);
      }

      setSnackbarMessage("Đã xác nhận gửi phiếu");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    }
  };

  const handleBqdConfirmation = async (transferReasonValue = "") => {
    if (bqdConfirmed || isConfirming) return;

    setIsConfirming(true);

    try {
      // Prepare API data
      const chiTietSanPham = receipt.items
        .filter(
          (item) =>
            item.maVt && item.slThucNhan !== null && item.slThucNhan !== ""
        )
        .map((item) => ({
          maSP: item.maVt,
          slThucNhan: parseInt(item.slThucNhan) || 0,
          LoSX: item.loSX || "",
          LoaiBanh: TRUNG_THU_LOAI_BANH,
          LoaiPhieu: TRUNG_THU_LOAI_PHIEU,
        }));

      if (chiTietSanPham.length === 0) {
        setSnackbarMessage(
          "Vui lòng nhập số lượng thực nhận cho ít nhất một sản phẩm"
        );
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        setIsConfirming(false);
        return;
      }

      const confirmUserName = getCurrentConfirmUser();
      const requestBody = {
        LoaiBanh: TRUNG_THU_LOAI_BANH,
        LoaiPhieu: TRUNG_THU_LOAI_PHIEU,
        NguoiXacNhan: confirmUserName,
        maPhieuNhap: id,
        chiTietSanPham: chiTietSanPham,
      };

      if (isTransferReceiptData(receipt)) {
        requestBody.NDXacNhan = transferReasonValue.trim();
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/xac-nhan-kiem-soat`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || "Failed to confirm control");
      }

      const now = new Date();
      const confirmedAt =
        parseDateTime(responseData?.data?.TimeXacNhan) ||
        parseDateTime(responseData?.TimeXacNhan) ||
        now;

      // Update states
      setBqdConfirmed(true);
      setBqdTimestamp(confirmedAt);
      setBqdConfirmedBy(confirmUserName);

      // Update receipt state
      setReceipt((prev) => {
        const isTransfer = isTransferReceiptData(prev);
        return {
          ...prev,
          actPhieu: true,
          status: isTransfer ? 2 : prev?.status,
          bqdApproved: true,
          bqdTimestamp: confirmedAt,
          bqdMessage: "PKD đã xác nhận",
          nguoiXacNhan: confirmUserName,
          ndXacNhan: isTransfer ? transferReasonValue.trim() : prev.ndXacNhan,
          trangThai: isTransfer ? "Chờ cửa hàng nhận" : "Đã duyệt",
          lastUpdated: now,
          updateCount: (prev.updateCount || 0) + 1,
        };
      });

      // Update localStorage
      try {
        const storedReceipts =
          JSON.parse(localStorage.getItem("phieuNhapList")) || [];
        const updatedReceipts = storedReceipts.map((r) => {
          if (r.id === parseInt(id)) {
            return {
              ...r,
              bqdApproved: true,
              bqdTimestamp: confirmedAt,
              bqdMessage: "PKD đã xác nhận",
              nguoiXacNhan: confirmUserName,
              ndXacNhan: isTransferReceiptData(r)
                ? transferReasonValue.trim()
                : r.ndXacNhan,
              status: isTransferReceiptData(r) ? 2 : r.status,
              trangThai: isTransferReceiptData(r)
                ? "Chờ cửa hàng nhận"
                : "Đã duyệt",
              lastUpdated: now,
              updateCount: (r.updateCount || 0) + 1,
            };
          }
          return r;
        });
        localStorage.setItem("phieuNhapList", JSON.stringify(updatedReceipts));
      } catch (error) {
        console.error("Error updating localStorage:", error);
      }

      setSnackbarMessage(
        `Xác nhận kiểm soát thành công! Đã cập nhật ${responseData.data.soSanPhamCapNhat} sản phẩm.`
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error confirming control:", error);
      setSnackbarMessage(`Lỗi khi xác nhận kiểm soát: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleAdminConfirmationClick = () => {
    if (isTransferReceiptData(receipt)) {
      setTransferReason("");
      setOpenTransferReasonModal(true);
      return;
    }

    handleBqdConfirmation();
  };

  const handleSaveTransferReason = async () => {
    const normalizedReason = transferReason.trim();
    if (!normalizedReason) {
      setSnackbarMessage("Vui lòng nhập lý do điều chuyển");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    setOpenTransferReasonModal(false);
    await handleBqdConfirmation(normalizedReason);
  };

  const handleReceiverConfirmation = () => {
    if (receiverConfirmed) return;

    const now = new Date();
    const confirmUserName = getCurrentConfirmUser();
    setReceiverConfirmed(true);
    setReceiverTimestamp(now);
    setReceiverConfirmedBy(confirmUserName);

    // Update receipt state
    setReceipt((prev) => ({
      ...prev,
      receiverConfirmed: true,
      receiverTimestamp: now,
      receiverMessage: "Đã xác nhận gửi phiếu",
      tenUser: confirmUserName,
      lastUpdated: now,
      updateCount: (prev.updateCount || 0) + 1,
    }));

    // Update localStorage
    try {
      const storedReceipts =
        JSON.parse(localStorage.getItem("phieuNhapList")) || [];
      const updatedReceipts = storedReceipts.map((r) => {
        if (r.id === parseInt(id)) {
          return {
            ...r,
            receiverConfirmed: true,
            receiverTimestamp: now,
            receiverMessage: "Đã xác nhận gửi phiếu",
            tenUser: confirmUserName,
            lastUpdated: now,
            updateCount: (r.updateCount || 0) + 1,
          };
        }
        return r;
      });
      localStorage.setItem("phieuNhapList", JSON.stringify(updatedReceipts));
    } catch (error) {
      console.error("Error updating localStorage:", error);
    }

    setSnackbarMessage("Đã xác nhận gửi phiếu");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  // Handle opening the add product dialog
  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
    setFormErrors({});
  };
  // Handle closing the add product dialog
  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  // --- new: open/close and save handlers for store modal ---
  const handleOpenStoreModal = async () => {
    setOpenStoreModal(true);
    setSelectedStoreMaCH(receipt?.maCH ? String(receipt.maCH) : "");
    setCustomerName(receipt?.tenKH || "");
    if (storeOptions.length === 0) {
      setStoreOptionsLoading(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/stores`);
        const data = res.ok ? await res.json() : [];
        const apiStoreOptions = Array.isArray(data) ? data : [];
        setStoreOptions([
          ...apiStoreOptions.filter(
            (store) => !CUSTOM_STORE_OPTIONS.some(
              (customStore) =>
                getStoreIdValue(store) === String(customStore.IDCH)
            )
          ),
          ...CUSTOM_STORE_OPTIONS,
        ]);
      } catch (e) {
        // fallback to previously loaded stores list
        const fallbackStoreOptions = Array.isArray(stores) ? stores : [];
        setStoreOptions([
          ...fallbackStoreOptions.filter(
            (store) => !CUSTOM_STORE_OPTIONS.some(
              (customStore) =>
                getStoreIdValue(store) === String(customStore.IDCH)
            )
          ),
          ...CUSTOM_STORE_OPTIONS,
        ]);
      } finally {
        setStoreOptionsLoading(false);
      }
    }
  };

  const handleCloseStoreModal = () => {
    setOpenStoreModal(false);
    setStoreSearch("");
    setCustomerName("");
  };

  const handleSaveStoreChange = async () => {
    if (!selectedStoreMaCH) return;
    const tenKH = customerName.trim();
    if (requiresCustomerName(selectedStoreMaCH) && !tenKH) {
      setSnackbarMessage("Vui lòng điền khách hàng (biếu tặng)");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    try {
      setIsConfirming(true);
      const soPN = receipt?.soPhieu || `PN${id}`;
      const soPNNumberOnly = soPN.replace(/\D/g, ``);

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/update-store`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          SoPN: soPNNumberOnly,
          MaCH: selectedStoreMaCH,
          TenKH: tenKH || String(receipt?.tenKH || "").trim(),
          LoaiBanh: TRUNG_THU_LOAI_BANH,
          LoaiPhieu: TRUNG_THU_LOAI_PHIEU,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || "Cập nhật cửa hàng thất bại");
      }

      const newName = getStoreNameByMaCH(
        selectedStoreMaCH,
        storeOptions.length ? storeOptions : stores
      );
      setStoreName(newName);
      setReceipt((prev) => ({
        ...prev,
        khachHang: newName,
        maCH: selectedStoreMaCH,
        tenKH: tenKH || prev?.tenKH || "",
      }));
      setSnackbarMessage("Cập nhật cửa hàng thành công");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setOpenStoreModal(false);
    } catch (err) {
      setSnackbarMessage(`Lỗi cập nhật cửa hàng: ${err.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsConfirming(false);
    }
  };
  // ----------------------------------------------------------

  const updateReceipt = async () => {
    try {
      const maCHCT = toValidStoreId(receipt?.maCH);
      if (maCHCT === null) {
        setSnackbarMessage("Mã cửa hàng của phiếu nhập không hợp lệ, không thể cập nhật!");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      const requestBody = {
        MaCH: maCHCT,
        MaCH_CT: maCHCT,
        LoaiBanh: TRUNG_THU_LOAI_BANH,
        LoaiPhieu: TRUNG_THU_LOAI_PHIEU,
        Items: receipt.items.map((item) => ({
          MaSP: item.maVt,
          TenSP: item.ten,
          SoLuong: parseInt(item.slThucNhan, 10),
          GiaMua: item.giaMua || 0,
          GiamPT: item.giamPT || 0,
          ThanhTien: parseInt(item.slThucNhan, 10) * (item.giaMua || 0),
          LoSX: item.loSX || "",
          MaCH: maCHCT,
          MaCH_CT: maCHCT,
          LoaiBanh: TRUNG_THU_LOAI_BANH,
          LoaiPhieu: TRUNG_THU_LOAI_PHIEU,
        })),
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/sale-nhap-hang/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update receipt");
      }

      setSnackbarMessage("Cập nhật phiếu nhập thành công!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error updating receipt:", error);
      setSnackbarMessage("Có lỗi xảy ra khi cập nhật phiếu nhập!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Add new function for store confirmation
  const handleStoreConfirmation = async () => {
    if (isConfirming) return;

    if (!isTransferReceiptData(receipt) && storeConfirmed) return;

    if (isTransferReceiptData(receipt) && isStatusValue(receipt?.status, 1)) {
      return;
    }

    if (isTransferReceiptData(receipt) && !isTransferReadyForStoreConfirmation()) {
      setSnackbarMessage("Phiếu điều chuyển chưa đủ điều kiện xác nhận nhận hàng");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    setIsConfirming(true);

    try {
      // Check if ALL product types with SoLuong have SLThucNhan values before proceeding
      const productTypesWithSoLuongButMissingSLThucNhan = receipt.items.filter(
        (item) =>
          item.maVt &&
          item.soLuong &&
          item.soLuong > 0 &&
          (item.slThucNhan === null ||
            item.slThucNhan === undefined ||
            item.slThucNhan === "")
      );

      if (productTypesWithSoLuongButMissingSLThucNhan.length > 0) {
        setSnackbarMessage(
          `Vui lòng nhập số lượng thực nhận cho tất cả loại bánh có số lượng. Còn thiếu ${productTypesWithSoLuongButMissingSLThucNhan.length} loại bánh chưa có số lượng thực nhận.`
        );
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        setIsConfirming(false);
        return;
      }

      // Prepare API data - only include items that have SoLuong and SLThucNhan
      const chiTietSanPham = receipt.items
        .filter(
          (item) =>
            item.maVt &&
            item.soLuong &&
            item.soLuong > 0 &&
            item.slThucNhan !== null &&
            item.slThucNhan !== undefined &&
            item.slThucNhan !== ""
        )
        .map((item) => ({
          maSP: item.maVt,
          soLuong: parseInt(item.slThucNhan) || 0, // Set SoLuong = SLThucNhan
          slThucNhan: parseInt(item.slThucNhan) || 0,
          LoSX: item.loSX || "",
          LoaiBanh: TRUNG_THU_LOAI_BANH,
          LoaiPhieu: TRUNG_THU_LOAI_PHIEU,
        }));

      const requestBody = {
        LoaiBanh: TRUNG_THU_LOAI_BANH,
        LoaiPhieu: TRUNG_THU_LOAI_PHIEU,
        maPhieuNhap: id,
        NguoiXacNhan: localStorage.getItem("TenUser") || "Store",
        chiTietSanPham: chiTietSanPham,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/xac-nhan-nhan-hang`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || "Failed to confirm receipt");
      }

      const now = new Date();

      // Update states
      setStoreConfirmed(true);
      setStoreTimestamp(now);
      setTrangThai(
        isTransferReceiptData(receipt) ? "Hoàn tất" : "Cửa hàng đã xác nhận"
      );

      // Update receipt state
      setReceipt((prev) => ({
        ...prev,
        status: 1,
        storeConfirmed: true,
        storeTimestamp: now,
        storeMessage: "Đã xác nhận nhận hàng",
        trangThai: isTransferReceiptData(prev)
          ? "Hoàn tất"
          : "Cửa hàng đã xác nhận",
        lastUpdated: now,
        updateCount: (prev.updateCount || 0) + 1,
      }));

      setSnackbarMessage(
        `Xác nhận nhận hàng thành công! Đã cập nhật ${responseData.data?.soSanPhamCapNhat || chiTietSanPham.length
        } loại bánh.`
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error confirming receipt:", error);
      setSnackbarMessage(`Lỗi khi xác nhận nhận hàng: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsConfirming(false);
    }
  };

  // Check if user can edit SL Thực Nhận - also consider status
  const canEditSlThucNhan = () => {
    if (isTransferReceiptData(receipt)) {
      return (
        userRole === "user" &&
        isCurrentUserReceivingStore() &&
        isTransferReadyForStoreConfirmation()
      );
    }

    return (
      userRole === "user" &&
      !storeConfirmed &&
      !bqdConfirmed &&
      (isStatusValue(receipt?.status, 1) || isStatusValue(receipt?.status, 2))
    );
  };

  // Check if add product button should be shown
  const shouldShowAddProduct = () => {
    return userRole !== "coordinator" && !isEditMode;
  };

  // Check if edit button should be shown
  const shouldShowEditButton = () => {
    return userRole === "coordinator" || userRole === "admin";
  };

  // Only allow edit store for MaPB in (4, 20, 80)
  const canEditStore = React.useMemo(() => {
    const maPB = String(userInfo?.maPB || localStorage.getItem("MaPB") || "").trim();
    return ["4", "321", "16", "80"].includes(maPB);
  }, [userInfo?.maPB]);

  // Helper function to check if SL Thực Nhận differs from Số Lượng
  const isSlThucNhanDiff = (item) => {
    // Only highlight if both values are numbers and not empty/null
    if (
      item &&
      item.soLuong !== undefined &&
      item.soLuong !== null &&
      item.soLuong !== "" &&
      item.slThucNhan !== undefined &&
      item.slThucNhan !== null &&
      item.slThucNhan !== ""
    ) {
      // Compare as numbers
      return Number(item.soLuong) !== Number(item.slThucNhan);
    }
    return false;
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Typography>Đang tải phiếu nhập...</Typography>
      </Container>
    );
  }

  if (!receipt) {
    return (
      <Container maxWidth="lg">
        <Typography>Không tìm thấy phiếu nhập</Typography>
      </Container>
    );
  }

  // Keep receipt items in one vertical list so scanned batches are easy to review.
  const organizeItemsIntoColumns = () => {
    if (!receipt || !receipt.items) {
      console.log("⚠️ No receipt or items found for organizing columns");
      return [];
    }

    console.log("📊 Organizing items into a vertical list:", receipt.items);
    return [receipt.items];
  };

  // Replace the existing chunkItems variable with organizedColumns
  const organizedColumns = organizeItemsIntoColumns();
  const receiptTotals = calculateReceiptTotals(receipt.items);
  const isMobile = window.innerWidth <= 768;
  const apiConfirmUser = getFirstConfirmUser(
    receipt?.nguoiXacNhan,
    receipt?.NguoiXacNhan,
    receipt?.nguoixacnhan
  );
  const apiSenderUser = getFirstConfirmUser(
    receipt?.tenUser,
    receipt?.TenUser,
    receipt?.tenuser
  );
  const receiverConfirmText = prefixConfirmUser(
    "Đã gửi phiếu",
    getFirstConfirmUser(receiverConfirmedBy, apiSenderUser)
  );
  const bqdConfirmText = prefixConfirmUser(
    "PKD đã xác nhận",
    getFirstConfirmUser(bqdConfirmedBy, apiConfirmUser)
  );
  const senderSignatureTitle = isTransferReceiptData(receipt)
    ? String(receipt?.maCH_DC ?? "").trim() === "29"
      ? "Tổ điều phối"
      : "Cửa hàng gửi"
    : "Điều phối";
  const currentBqdTimestamp =
    parseDateTime(receipt?.bqdTimestamp) || parseDateTime(bqdTimestamp);
  const currentReceiverTimestamp =
    parseDateTime(receipt?.receiverTimestamp) ||
    parseDateTime(receiverTimestamp) ||
    parseDateTime(receipt?.ngay);
  const isBqdConfirmedForWaiting = isTruthyFlag(
    receipt?.actPhieu || receipt?.bqdApproved || bqdConfirmed
  );
  const waitingStartAt = isBqdConfirmedForWaiting
    ? currentBqdTimestamp || currentReceiverTimestamp
    : currentReceiverTimestamp;
  const waitingEndAt = waitingStartAt ? currentTime : null;
  const hasStoreConfirmed =
    isStatusValue(receipt?.status, 1) ||
    storeConfirmed ||
    [
      "Cửa hàng đã xác nhận",
      "Đã nhận, có chênh lệch",
      "Tự động nhận",
      "Hoàn tất",
    ].includes(receipt?.trangThai);
  const hasStoreReceiveTimedOut =
    !hasStoreConfirmed &&
    !isReturnReceiptData(receipt) &&
    (!isTransferReceiptData(receipt) || isBqdConfirmedForWaiting) &&
    waitingStartAt !== null &&
    currentTime.getTime() - waitingStartAt.getTime() >
      STORE_RECEIVE_TIMEOUT_MS;
  const displayReceiptStatus = hasStoreReceiveTimedOut
    ? "Tự động nhận"
    : getReceiptStatus();
  const waitingTimeText =
    waitingStartAt && waitingEndAt
      ? formatElapsedDuration(waitingStartAt, waitingEndAt)
      : "Chưa có dữ liệu";
  const waitingTimeToneClass = getWaitingTimeToneClass(
    waitingStartAt,
    waitingEndAt
  );

  // Format date for display
 const displayDate = receipt?.receiverTimestamp || receipt?.ngay;

const day = displayDate instanceof Date ? displayDate.getDate() : "--";
const month = displayDate instanceof Date ? displayDate.getMonth() + 1 : "--";
const year = displayDate instanceof Date ? displayDate.getFullYear() : "----";


  // Format update timestamp for display
  const formatUpdateHistory = () => {
    if (!receipt || !receipt.lastUpdated) return null;

    const updateDate = new Date(receipt.lastUpdated);
    const formattedDate = updateDate.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    const updateCount = receipt.updateCount || 1;

    return `Cập nhật lần cuối: ${formattedDate} (Số lần cập nhật: ${updateCount})`;
  };

  return (
    <Box>
      {/* Header actions - only visible when not printing */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          "@media print": { display: "none" },
          "@media (max-width: 440px)": {
            p: 1,
            gap: 1,
          },
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
          variant="outlined"
          sx={{
            "@media (max-width: 440px)": {
              minWidth: "60px",
              padding: "4px 8px",
              fontSize: "10px",
              "& .MuiButton-startIcon": {
                marginRight: "2px",
                "& > *:nth-of-type(1)": {
                  fontSize: "14px",
                },
              },
            },
          }}
        >
          <span
            style={{ display: window.innerWidth <= 440 ? "none" : "inline" }}
          >
            Quay lại
          </span>
        </Button>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            "@media (max-width: 440px)": {
              gap: 0.5,
            },
          }}
        >
          {shouldShowEditButton() && (
            <Button
              startIcon={<EditIcon />}
              onClick={handleEdit}
              variant="outlined"
              sx={{
                mr: 1,
                "@media (max-width: 440px)": {
                  mr: 0,
                  minWidth: "60px",
                  padding: "4px 8px",
                  fontSize: "10px",
                  "& .MuiButton-startIcon": {
                    marginRight: "2px",
                    "& > *:nth-of-type(1)": {
                      fontSize: "14px",
                    },
                  },
                },
              }}
            >
              <span
                style={{
                  display: window.innerWidth <= 440 ? "none" : "inline",
                }}
              >
                Chỉnh sửa
              </span>
            </Button>
          )}
          <Button
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            variant="contained"
            color="primary"
            sx={{
              "@media (max-width: 440px)": {
                minWidth: "60px",
                padding: "4px 8px",
                fontSize: "10px",
                "& .MuiButton-startIcon": {
                  marginRight: "2px",
                  "& > *:nth-of-type(1)": {
                    fontSize: "14px",
                  },
                },
              },
            }}
          >
            <span
              style={{ display: window.innerWidth <= 440 ? "none" : "inline" }}
            >
              In phiếu
            </span>
          </Button>
        </Box>
      </Box>

      {/* Receipt layout based on the provided HTML */}
      <div className="receipt">
        <div className="header">
          <div className="logo">
            DOTI
            <br />
            COM
          </div>
          <div className="title">PHIẾU GIAO HÀNG</div>
          <div className="subtitle">20 20 30 90 96</div>
        </div>

        <div className="info-section">
          <div className="info-left">
            {isTransferReceiptData(receipt) && (
              <div>
                Người gửi:{" "}
                <span className="handwritten">
                  {senderName ||
                    getStoreNameByMaCH(receipt?.maCH_DC, stores, {
                      fallbackToLocalStorage: false,
                    }) ||
                    ""}
                </span>
              </div>
            )}
            <div>
              Khách hàng / Bakery:{" "}
              <span className="handwritten">
                {storeName || receipt.khachHang}
              </span>
              {/* edit button next to store name only for MaPB in (4,20,80) */}
              {canEditStore && (
                <IconButton
                  size="small"
                  onClick={handleOpenStoreModal}
                  sx={{ ml: 1, "@media print": { display: "none" } }}
                  aria-label="Chỉnh sửa cửa hàng"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </div>
            <div>
              Ngày: <span className="handwritten">{day}</span> tháng{" "}
              <span className="handwritten">{month}</span> năm{" "}
              <span className="handwritten">{year}</span>
            </div>
          </div>
          <div className="info-right">
            <div>Ký hiệu: DT/BN</div>
            <div>Số: {receipt.soPhieu}</div>
            <div>
              Tên khách đặt/ mã đơn đặt:{" "}
              <span className="handwritten">{receipt.tenKH || ""}</span>
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="items-table">
            <thead>
              <tr>
                {isMobile ? (
                  // Mobile layout: Single set of 4 columns
                  <>
                    <th className="code-col">Mã VT</th>
                    <th className="name-col">Tên Mặt Hàng</th>
                    <th className="qty-col">Số Lượng</th>
                    <th className="qty-col">SL Thực Nhận</th>
                  </>
                ) : (
                  // Desktop layout: Multiple column sets
                  organizedColumns.map((_, colIndex) => (
                    <React.Fragment key={`header-${colIndex}`}>
                      <th className="code-col">Mã vt</th>
                      <th className="name-col">Tên Mặt Hàng</th>
                      <th className="qty-col">Số Lượng</th>
                      <th className="qty-col">SL Thực Nhận</th>
                    </React.Fragment>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              {isMobile
                ? // Mobile layout: Simple rows
                organizedColumns[0]?.map((item, rowIndex) => (
                  <tr key={`mobile-row-${rowIndex}`}>
                    <td>{item.maVt}</td>
                    <td>{item.ten}</td>
                    <td className="handwritten">
                      <input
                        type="text"
                        value={item.soLuong || ""}
                        readOnly={true}
                        title="Số lượng"
                        style={
                          isSlThucNhanDiff(item)
                            ? { backgroundColor: "#fff3cd" }
                            : undefined
                        }
                      />
                    </td>
                    <td className="handwritten">
                      <input
                        type="text"
                        value={item.slThucNhan || ""}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          const newItems = [...receipt.items];
                          if (newItems[rowIndex]) {
                            newItems[rowIndex] = {
                              ...newItems[rowIndex],
                              slThucNhan: newValue,
                            };
                            setReceipt({ ...receipt, items: newItems });
                          }
                        }}
                        title="Số lượng thực nhận"
                        disabled={!canEditSlThucNhan()}
                        style={
                          isSlThucNhanDiff(item)
                            ? { backgroundColor: "#fff3cd" }
                            : undefined
                        }
                      />
                    </td>
                  </tr>
                ))
                : // Desktop layout: Complex multi-column rows
                Array.from({ length: organizedColumns[0]?.length || 0 }).map(
                  (_, rowIndex) => (
                    <tr key={`row-${rowIndex}`}>
                      {organizedColumns.map((column, colIndex) => {
                        const item = column[rowIndex];
                        if (!item) return null;

                        return (
                          <React.Fragment
                            key={`item-${rowIndex}-${colIndex}`}
                          >
                            <td>{item.maVt}</td>
                            <td>{item.ten}</td>
                            <td className="handwritten">
                              <input
                                type="text"
                                value={item.soLuong || ""}
                                readOnly={true}
                                title="Số lượng"
                                style={
                                  isSlThucNhanDiff(item)
                                    ? { backgroundColor: "#fff3cd" }
                                    : undefined
                                }
                              />
                            </td>
                            <td className="handwritten">
                              <input
                                type="text"
                                value={item.slThucNhan || ""}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  const newItems = [...receipt.items];
                                  const originalIndex =
                                    colIndex *
                                    (organizedColumns[0]?.length || 0) +
                                    rowIndex;
                                  if (newItems[originalIndex]) {
                                    newItems[originalIndex] = {
                                      ...newItems[originalIndex],
                                      slThucNhan: newValue,
                                    };
                                    setReceipt({
                                      ...receipt,
                                      items: newItems,
                                    });
                                  }
                                }}
                                title="Số lượng thực nhận"
                                disabled={!canEditSlThucNhan()}
                                style={
                                  isSlThucNhanDiff(item)
                                    ? { backgroundColor: "#fff3cd" }
                                    : undefined
                                }
                              />
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  )
                )}
            </tbody>
          </table>

          {/* Add custom product button - only visible when not printing and based on permissions */}
          {shouldShowAddProduct() && !storeConfirmed && (
            <Box
              sx={{
                mt: 2,
                textAlign: "center",
                "@media print": { display: "none" },
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                startIcon={<QrCodeScannerIcon />}
                onClick={handleOpenAddDialog}
                size="small"
              >
                Quét QR thêm sản phẩm
              </Button>
            </Box>
          )}
        </div>

        <div className="total-section">
          <div className="receipt-totals">
            <div>
              <strong>Tổng số lượng: </strong>
              <span className="handwritten">
                {numberFormatter.format(receiptTotals.quantity)}
              </span>
            </div>
            <div>
              <strong>Tổng tiền: </strong>
              <span className="handwritten">
                {currencyFormatter.format(receiptTotals.amount)}
              </span>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <strong>Trạng thái: </strong>
              <span
                className={`status-badge ${displayReceiptStatus
                  .toLowerCase()
                  .replace(/\s/g, "-")}`}
              >
                {displayReceiptStatus}
              </span>
            </div>
            <div>
              <strong>
                <span
                  className={`handwritten waiting-time-badge ${waitingTimeToneClass}`}
                >
                  Thời gian chờ: {waitingTimeText}
                </span>
              </strong>
            </div>
          </div>

          {!isEditMode && formatUpdateHistory() && (
            <div className="update-history">{formatUpdateHistory()}</div>
          )}
        </div>

        <div className="signature-section">
          {/* Coordinator signature box - only for coordinators or if already confirmed */}
          {(userRole === "coordinator" ||
            (userRole !== "coordinator" && receiverConfirmed)) && (
              <div className="signature-box">
                <div className="signature-title">
                  {senderSignatureTitle}
                  <br />
                  
                </div>
                {userRole === "coordinator" ? (
                  <>
                    {isEditMode ? (
                      <button
                        className="confirm-btn confirmed"
                        onClick={updateReceipt}
                      >
                        Cập nhật phiếu gửi
                      </button>
                    ) : (
                      <>
                        {!receiverConfirmed ? (
                          <button
                            className="confirm-btn"
                            onClick={handleReceiverConfirmation}
                          >
                            Xác nhận gửi phiếu
                          </button>
                        ) : (
                          <div className="status-text">
                            <div className="confirmed-text">
                              ✓ {receiverConfirmText}
                            </div>
                            {receiverConfirmText} vào ngày
                            <br />
                            {receiverTimestamp &&
                              new Date(receiverTimestamp).toLocaleString("vi-VN")}
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <div className="status-text">
                    <div className="confirmed-text">
                      ✓ {receiverConfirmText}
                    </div>
                    {receiverConfirmText} vào ngày
                    <br />
                    {receiverTimestamp &&
                      new Date(receiverTimestamp).toLocaleString("vi-VN")}
                  </div>
                )}
              </div>
            )}

          {/* BQD signature box - only for admins or if already confirmed */}
          {(userRole === "admin" || (userRole !== "admin" && bqdConfirmed)) && (
            <div className="signature-box">
              <div className="signature-title">
                {isTransferReceiptData(receipt)
                  ? "PKD/ Admin xác nhận"
                  : "PKD xác nhận"}
                <br />
                
              </div>
              {receipt.ndXacNhan && (
                <div className="status-text">
                  Lý do điều chuyển: {receipt.ndXacNhan}
                </div>
              )}
              {userRole === "admin" ? (
                <>
                  <button
                    className={`confirm-btn ${bqdConfirmed ? "confirmed" : ""}`}
                    onClick={handleAdminConfirmationClick}
                    disabled={bqdConfirmed || isConfirming}
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    {isConfirming && (
                      <CircularProgress size={16} sx={{ color: "white" }} />
                    )}
                    {bqdConfirmed
                      ? "✓ Đã xác nhận"
                      : isConfirming
                        ? "Đang xử lý..."
                        : "Xác nhận kiểm soát"}
                  </button>
                  {bqdConfirmed && bqdTimestamp && (
                    <div className="status-text">
                      {bqdConfirmText}
                      <br />
                      {new Date(bqdTimestamp).toLocaleString("vi-VN")}
                    </div>
                  )}
                </>
              ) : (
                <div className="status-text">
                  <div className="confirmed-text">✓ {bqdConfirmText}</div>
                  {bqdConfirmText} vào ngày
                  <br />
                  {bqdTimestamp &&
                    new Date(bqdTimestamp).toLocaleString("vi-VN")}
                </div>
              )}
            </div>
          )}

          {/* Store confirmation button - only for receiving store users */}
          {shouldShowUserStoreConfirmation() && (
            <div className="signature-box">
              <div className="signature-title">
                Cửa hàng nhận xác nhận nhận hàng
                <br />
                
              </div>
              {shouldShowStoreConfirmationButton() ? (
                <button
                  className="confirm-btn"
                  onClick={handleStoreConfirmation}
                  disabled={isConfirming}
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  {isConfirming && (
                    <CircularProgress size={16} sx={{ color: "white" }} />
                  )}
                  {isConfirming
                    ? "Đang xử lý..."
                    : isTransferReceiptData(receipt)
                      ? "Cửa hàng xác nhận nhận hàng"
                      : "Xác nhận nhận hàng"}
                </button>
              ) : isStoreConfirmationComplete() ? (
                <div className="status-text">
                  <div className="confirmed-text">✓ Đã xác nhận nhận hàng</div>
                  Đã nhận hàng vào ngày
                  <br />
                  {storeTimestamp &&
                    new Date(storeTimestamp).toLocaleString("vi-VN")}
                </div>
              ) : (
                <div className="status-text">
                  <div className="pending-confirmation">
                    Chờ cửa hàng nhận xác nhận
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Show confirmed status for store confirmation if user is not `user' but store has confirmed */}
          {userRole !== "user" && isStoreConfirmationComplete() && (
            <div className="signature-box">
              <div className="signature-title">
                Cửa hàng nhận xác nhận nhận hàng
                <br />
                
              </div>
              <div className="status-text">
                <div className="confirmed-text">✓ Đã xác nhận nhận hàng</div>
                Đã nhận hàng vào ngày
                <br />
                {storeTimestamp &&
                  new Date(storeTimestamp).toLocaleString("vi-VN")}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* --- new: Dialog for selecting store --- */}
      <Dialog
        open={openStoreModal}
        onClose={handleCloseStoreModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Chọn cửa hàng</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: "grid", gap: 2 }}>
            <TextField
              label="Tìm kiếm cửa hàng"
              placeholder="Nhập tên cửa hàng để lọc"
              value={storeSearch}
              onChange={(e) => setStoreSearch(e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="store-select-label">Cửa hàng</InputLabel>
              <Select
                labelId="store-select-label"
                label="Cửa hàng"
                value={selectedStoreMaCH}
                onChange={(e) => setSelectedStoreMaCH(e.target.value)}
                disabled={storeOptionsLoading}
              >
                {(storeOptions || [])
                  .filter((s) => {
                    const n = getStoreDisplayName(s).toLowerCase();
                    return n.includes(storeSearch.trim().toLowerCase());
                  })
                  .map((s, idx) => {
                    const id = getStoreIdValue(s);
                    const name = getStoreDisplayName(s) || id || `Store ${idx + 1}`;
                    return (
                      <MenuItem key={`${id || idx}`} value={id}>
                        {name}
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
            {requiresCustomerName(selectedStoreMaCH) && (
              <TextField
                label="Vui lòng điền khách hàng (biếu tặng)"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                required
                fullWidth
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStoreModal} color="inherit">
            Thoát
          </Button>
          <Button
            onClick={handleSaveStoreChange}
            variant="contained"
            disabled={
              !selectedStoreMaCH ||
              isConfirming ||
              (requiresCustomerName(selectedStoreMaCH) && !customerName.trim())
            }
          >
            {isConfirming ? "Đang lưu..." : "Lưu"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* --- end new dialog --- */}

      <Dialog
        open={openTransferReasonModal}
        onClose={() => setOpenTransferReasonModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Lý do điều chuyển</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Lý do điều chuyển"
            value={transferReason}
            onChange={(e) => setTransferReason(e.target.value)}
            margin="dense"
            multiline
            minRows={3}
            fullWidth
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTransferReasonModal(false)} color="inherit">
            Thoát
          </Button>
          <Button onClick={handleSaveTransferReason} variant="contained">
            Lưu
          </Button>
        </DialogActions>
      </Dialog>

      <TrungThuQrScannerModal
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        onScan={(qrData) =>
          processQRCode(qrData, { showErrorModal: false })
        }
        title="Thêm sản phẩm bằng QR Code"
      />

      {/* Error Modal */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle color="error">Lỗi</DialogTitle>
        <DialogContent>
          <Typography>{errorModalMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorModalOpen(false)} color="primary">
            Đóng
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: Arial, sans-serif;
          background-color: #f5f5f5;
          padding: 20px;
        }

        .receipt {
          max-width: 1200px;
          margin: 0 auto;
          background: white;
          padding: 20px;
          border:  2px solid #333;
          box-shadow: 0 0 10px rgba(0, 0, 0.1);
          width: 100%;
        }

        .header {
          text-align: center;
          margin-bottom: 20px;
          position: relative;
        }

        .logo {
          position: absolute;
          left: 0;
          top: 0;
          width: 60px;
          height: 60px;
          background: linear-gradient(45deg, #ff6b35, #f7931e);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 12px;
        }

        .title {
          color: #d32f2f;
          font-size: 28px;
          font-weight: bold;
          margin-top: 10px;
          letter-spacing: 2px;
        }

        .subtitle {
          font-style: italic;
          color: #666;
          margin-top: 5px;
        }

        .info-section {
          display: flex;
          justify-content: space-between;
          margin-bottom: 20px;
          border-bottom: 1px solid #ccc;
          padding-bottom: 10px;
        }

        .info-left {
          flex: 1;
        }

        .info-right {
          text-align: right;
        }

        .table-container {
          overflow-x: auto;
          margin-bottom: 30px;
          width: 100%;
        }

        .items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
          table-layout: fixed;
          min-width: 800px;
        }

        .items-table th,
        .items-table td {
          border: 1px solid #333;
          padding: 4px 6px;
          text-align: left;
          word-wrap: break-word;
        }

        .items-table th {
          background-color: #ffcccb;
          font-weight: bold;
          text-align: center;
        }

        .code-col {
          width: 60px;
        }

        .name-col {
          width: 140px;
        }

        .qty-col {
          width: 50px;
          text-align: center;
        }

        .handwritten {
          font-family: "Courier New", monospace;
          color: #0066cc;
          font-weight: bold;
        }

        .handwritten input {
          width: 100%;
          border: none;
          background: transparent;
          border-bottom: 1px dashed #999;
          color: #0066cc;
          font-weight: bold;
          text-align: center;
          padding: 2px;
          font-family: "Courier New", monospace;
          font-size: 12px;
          /* Highlight if SL Thực Nhận differs from Số Lượng */
          /* The inline style above will override this if needed */
        }

        .signature-section {
          display: flex;
          justify-content: space-around;
          gap: 20px;
          margin-top: 30px;
          padding-top: 20px;
          border-top: 2px solid #333;
        }

        .signature-box {
          text-align: center;
          padding: 20px 10px;
          border: 1px solid #ddd;
          border-radius: 8px;
          background-color: #f9f9f9;
          flex: 1;
        }

        .signature-title {
          font-weight: bold;
          margin-bottom: 15px;
          color: #333;
          font-size: 13px;
        }

        .confirm-btn {
          background: linear-gradient(45deg, #4caf50, #45a049);
          color: white;
          border: none;
          padding: 12px 20px;
          border-radius: 25px;
          cursor: pointer;
          font-size: 14px;
          font-weight: bold;
          transition: all 0.3s ease;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
          width: 100%;
          min-height: 48px;
        }

        .confirm-btn:hover {
          background: linear-gradient(45deg, #45a049, #4caf50);
          transform: translateY(-2px);
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
        }

        .confirm-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .confirm-btn.confirmed {
          background: linear-gradient(45deg, #2196f3, #1976d2);
          cursor: default;
        }

        .confirm-btn.confirmed:hover {
          transform: none;
          background: linear-gradient(45deg, #2196f3, #1976d2);
        }

        .status-text {
          font-size: 11px;
          margin-top: 8px;
          color: #666;
          font-style: italic;
        }

        .total-section {
          margin-top: 20px;
          text-align: right;
          padding: 10px;
          background-color: #f0f0f0;
          border-radius: 5px;
        }

        .receipt-totals {
          display: flex;
          justify-content: flex-end;
          gap: 24px;
          margin-bottom: 10px;
          font-size: 14px;
        }

        .status-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: bold;
          color: white;
        }

        .status-badge.đã-gửi-phiếu {
          background-color: #ff9800;
        }

        .status-badge.chờ-admin-xác-nhận {
          background-color: #9c27b0;
        }

        .status-badge.chờ-cửa-hàng-nhận {
          background-color: #ff9800;
        }

        .status-badge.đã-duyệt {
          background-color: #2196f3;
        }

        .status-badge.hoàn-tất {
          background-color: #4caf50;
        }

        .status-badge.hoàn-thành {
          background-color: #4caf50;
        }

        .status-badge.tự-động-nhận {
          background-color: #4caf50;
        }

        .waiting-time-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 5px 12px;
          border-radius: 999px;
          border: 1px solid;
          line-height: 1.2;
          font-weight: 700;
          white-space: nowrap;
        }

        .waiting-time-badge.waiting-neutral {
          color: #475569;
          background-color: #f1f5f9;
          border-color: #cbd5e1;
        }

        .waiting-time-badge.waiting-cool {
          color: #0369a1;
          background-color: #e0f2fe;
          border-color: #7dd3fc;
        }

        .waiting-time-badge.waiting-fresh {
          color: #166534;
          background-color: #dcfce7;
          border-color: #86efac;
        }

        .waiting-time-badge.waiting-warm {
          color: #92400e;
          background-color: #fef3c7;
          border-color: #fcd34d;
        }

        .waiting-time-badge.waiting-hot {
          color: #c2410c;
          background-color: #ffedd5;
          border-color: #fdba74;
        }

        .waiting-time-badge.waiting-critical {
          color: #b91c1c;
          background-color: #fee2e2;
          border-color: #fca5a5;
        }

        .update-history {
          font-size: 12px;
          font-style: italic;
          color: #666;
          margin-top: 8px;
          text-align: left;
        }

        .status-text .confirmed-text {
          color: #2196f3;
          font-weight: bold;
          font-size: 14px;
          margin-bottom: 5px;
        }

        .status-text .pending-confirmation {
          color: #ff9800;
          font-weight: bold;
         
          font-size: 14px;
        }

        /* Mobile responsive styles */
        @media (max-width: 768px) {
          body {
            padding: 10px;
          }

          .receipt {
            padding: 15px;
            max-width: 100%;
            border-width: 1px;
          }

          .header {
            margin-bottom: 15px;
          }

          .logo {
            width: 50px;
            height: 50px;
            font-size: 10px;
          }

          .title {
            font-size: 20px;
            margin-top: 5px;
            letter-spacing: 1px;
          }

          .subtitle {
            font-size: 12px;
          }

          .info-section {
            flex-direction: column;
            gap: 10px;
            margin-bottom: 15px;
          }

          .info-right {
            text-align: left;
          }

          .table-container {
            margin-bottom: 20px;
          }

          .items-table {
            font-size: 10px;
            min-width: 100%;
          }

          .items-table th,
          .items-table td {
            padding: 3px 4px;
          }

          .code-col {
            width: 20%;
          }

          .name-col {
            width: 40%;
          }

          .qty-col {
            width: 20%;
            text-align: center;
          }

          .handwritten input {
            font-size: 10px;
            padding: 1px;
          }

          .signature-section {
            flex-direction: column;
            gap: 15px;
            margin-top: 20px;
            padding-top: 15px;
          }

          .signature-box {
            padding: 15px 10px;
          }

          .signature-title {
            font-size: 12px;
            margin-bottom: 12px;
          }

          .confirm-btn {
            padding: 10px 15px;
            font-size: 12px;
            min-height: 44px;
          }

          .status-text {
            font-size: 10px;
          }

          .total-section {
            padding: 8px;
            text-align: center;
          }

          .status-badge {
            font-size: 11px;
            padding: 3px 8px;
          }

          .update-history {
            font-size: 10px;
          }
        }

        /* Extra small mobile devices */
        @media (max-width: 480px) {
          body {
            padding: 5px;
          }

          .receipt {
            padding: 10px;
          }

          .title {
            font-size: 18px;
          }

          .items-table {
            font-size: 9px;
            min-width: 100%;
          }

          .items-table th,
          .items-table td {
            padding: 2px 3px;
          }

          .code-col {
            width: 18%;
          }

          .name-col {
            width: 42%;
          }

          .qty-col {
            width: 20%;
          }

          .handwritten input {
            font-size: 9px;
          }

          .signature-box {
            padding: 12px 8px;
          }

          .confirm-btn {
            font-size: 11px;
            padding: 8px 12px;
            min-height: 40px;
          }

          .status-text {
            font-size: 9px;
          }

          .total-section {
            padding: 6px;
            text-align: center;
            font-size: 11px;
          }

          .status-badge {
            font-size: 9px;
            padding: 2px 6px;
          }

          .update-history {
            font-size: 8px;
            text-align: center;
          }

          .handwritten {
            font-size: 8px;
          }

          /* Specific adjustments for very narrow screens */
          .info-section {
            font-size: 10px;
          }

          .info-left,
          .info-right {
            text-align: left;
          }

          /* Make table more compact */
          .items-table th {
            font-size: 7px;
            padding: 1px;
          }

          /* Adjust button container for mobile */
          .signature-section .confirm-btn {
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
        }

        /* Ultra small mobile devices - 400px and below */
        @media (max-width: 400px) {
          body {
            padding: 3px;
            font-size: 12px;
          }

          .receipt {
            padding: 8px;
            border-width: 1px;
          }

          .header {
            margin-bottom: 10px;
          }

          .logo {
            width: 40px;
            height: 40px;
            font-size: 8px;
            position: relative;
            margin: 0 auto 10px;
          }

          .title {
            font-size: 16px;
            margin-top: 0;
            letter-spacing: 0.5px;
            line-height: 1.2;
          }

          .subtitle {
            font-size: 10px;
          }

          .info-section {
            flex-direction: column;
            gap: 8px;
            margin-bottom: 12px;
            font-size: 11px;
          }

          .info-left div,
          .info-right div {
            margin-bottom: 5px;
            border-width: 1px;
          }

          .code-col {
            width: 15%;
          }

          .name-col {
            width: 45%;
          }

          .qty-col {
            width: 20%;
          }

          .handwritten input {
            font-size: 8px;
            padding: 1px;
            min-height: 18px;
          }

          .signature-section {
            flex-direction: column;
            gap: 10px;
            margin-top: 15px;
            padding-top: 10px;
          }

          .signature-box {
            padding: 10px 6px;
            margin-bottom: 8px;
          }

          .signature-title {
            font-size: 10px;
            margin-bottom: 10px;
            line-height: 1.2;
          }

          .confirm-btn {
            font-size: 10px;
            padding: 6px 8px;
            min-height: 36px;
            border-radius: 18px;
            white-space: nowrap;
          }

          .status-text {
            font-size: 8px;
            line-height: 1.2;
          }

          .total-section {
            padding: 6px;
            text-align: center;
            font-size: 11px;
          }

          .status-badge {
            font-size: 9px;
            padding: 2px 6px;
          }

          .update-history {
            font-size: 8px;
            text-align: center;
          }

          .handwritten {
            font-size: 8px;
          }
        }

        /* Ultra narrow screens - 350px and below */
        @media (max-width: 350px) {
          .receipt {
            padding: 5px;
          }

          .title {
            font-size: 14px;
          }

          .items-table {
            min-width: 100%;
            font-size: 7px;
          }

          .code-col {
            width: 15%;
          }

          .name-col {
            width: 45%;
          }

          .qty-col {
            width: 20%;
          }

          .handwritten input {
            font-size: 7px;
            min-height: 16px;
          }

          .confirm-btn {
            font-size: 9px;
            padding: 5px 6px;
            min-height: 32px;
          }

          .signature-title {
            font-size: 9px;
          }

          .status-text {
            font-size: 7px;
          }

          .total-section {
            font-size: 10px;
          }
        }

        /* Landscape orientation on mobile */
        @media (max-width: 768px) and (orientation: landscape) {
        /* Landscape orientation on mobile */
        @media (max-width: 768px) and (orientation: landscape) {
          .signature-section {
            flex-direction: row;
            gap: 10px;
          }

          .signature-box {
            flex: 1;
            padding: 12px 8px;
          }

          .confirm-btn {
            font-size: 11px;
            padding: 8px 10px;
          }
        }

        /* Landscape for ultra small screens */
        @media (max-width: 400px) and (orientation: landscape) {
          .signature-section {
            flex-direction: row;
            gap: 8px;
            flex-direction: row;
            gap: 8px;
          }

          .signature-box {
            padding: 8px 4px;
          }

          .confirm-btn {
            font-size: 9px;
            padding: 4px 6px;
            min-height: 30px;
          }

          .signature-title {
            font-size: 8px;
            margin-bottom: 8px;
          }
        }

        @media (max-width: 768px) {
          body {
            padding: 6px;
          }

          .receipt {
            padding: 10px;
            border-width: 1px;
            font-size: 13px;
          }

          .header {
            margin-bottom: 10px;
          }

          .logo {
            width: 42px;
            height: 42px;
            font-size: 9px;
            position: relative;
            margin: 0 auto 8px;
          }

          .title {
            font-size: 21px;
            line-height: 1.15;
            letter-spacing: 0;
            margin-top: 0;
          }

          .subtitle {
            font-size: 12px;
          }

          .info-section {
            flex-direction: column;
            gap: 6px;
            margin-bottom: 10px;
            font-size: 13px;
            line-height: 1.45;
          }

          .info-left,
          .info-right {
            text-align: left;
          }

          .table-container {
            margin-bottom: 14px;
          }

          .items-table {
            min-width: 100%;
            font-size: 12px;
            table-layout: fixed;
          }

          .items-table th,
          .items-table td {
            padding: 5px 4px;
          }

          .items-table th {
            font-size: 11px;
          }

          .code-col {
            width: 18%;
          }

          .name-col {
            width: 44%;
          }

          .qty-col {
            width: 19%;
          }

          .handwritten {
            font-size: 12px;
          }

          .handwritten input {
            font-size: 13px;
            min-height: 28px;
            padding: 3px 1px;
          }

          .total-section {
            margin-top: 14px;
            padding: 8px;
            font-size: 12px;
            text-align: left;
          }

          .total-section > div {
            gap: 8px;
            align-items: center;
          }

          .receipt-totals {
            justify-content: space-between;
            flex-wrap: wrap;
            font-size: 12px;
          }

          .status-badge {
            font-size: 11px;
            padding: 3px 7px;
          }

          .signature-section {
            flex-direction: row;
            align-items: stretch;
            justify-content: space-between;
            gap: 6px;
            margin-top: 14px;
            padding-top: 10px;
          }

          .signature-box {
            flex: 1 1 0;
            min-width: 0;
            padding: 8px 4px;
            margin-bottom: 0;
            border-radius: 6px;
          }

          .signature-title {
            font-size: 10px;
            line-height: 1.2;
            margin-bottom: 7px;
          }

          .confirm-btn {
            min-height: 34px;
            padding: 6px 4px;
            border-radius: 16px;
            font-size: 10px;
            line-height: 1.15;
            white-space: normal;
            overflow-wrap: anywhere;
          }

          .status-text {
            font-size: 9px;
            line-height: 1.2;
          }

          .status-text .confirmed-text,
          .status-text .pending-confirmation {
            font-size: 10px;
            line-height: 1.2;
          }
        }

        @media (max-width: 360px) {
          .receipt {
            padding: 8px 5px;
          }

          .title {
            font-size: 19px;
          }

          .items-table {
            font-size: 11px;
          }

          .items-table th,
          .items-table td {
            padding: 4px 2px;
          }

          .handwritten input {
            font-size: 12px;
            min-height: 26px;
          }

          .signature-section {
            gap: 4px;
          }

          .signature-box {
            padding: 6px 3px;
          }

          .signature-title,
          .confirm-btn {
            font-size: 9px;
          }

          .status-text {
            font-size: 8px;
          }
        }

        @media print {
          body {
            padding: 0;
          }
          .receipt {
            max-width: 100%;
            border: none;
            box-shadow: none;
          }
          .handwritten input {
            border-bottom: 1px solid #999;
          }
          .items-table {
            font-size: 10px;
          }
        }
      `}</style>
    </Box>
  );
};

export default PhieuNhapTT;
